import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Trophy,
  MapPin,
  Award,
} from 'lucide-react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress
} from '@mui/material';
import theme from '../theme';
import ApiService from '../services/api';

const Winners = () => {
  const { t } = useTranslation('winners');
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        setLoading(true);
        const data = await ApiService.getWinners();
        setWinners(data.winners || []);
      } catch (error) {
        console.error('Error fetching winners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWinners();
  }, []);

  // Filter winners based on search and filters
  const filteredWinners = useMemo(() => {
    let filtered = winners;
    return filtered;
  }, [winners]);

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: "160px", lg: "120px" },
          pb: { xs: "60px", lg: "80px" },
          minHeight: { xs: '50vh', md: '70vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'visible',
          backgroundImage: `url("/assets/awards_bg.png")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5)  0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3,
                background: `linear-gradient(135deg, #1A1A1A 30%, ${theme.palette.primary.main} 50%, #1A1A1A 70%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 4,
                opacity: 0.9,
                maxWidth: 800,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.8,
                maxWidth: 600,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 6, md: 0 }, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('stats.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 4
                }}
              >
                {t('stats.sectionTitle')}
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid size={{ xs: 12, lg: 2.4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0 }}
                >
                  <Card sx={{
                    p: 3,
                    textAlign: { xs: 'left', lg: 'center' },
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(212, 167, 71, 0.1)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: { xs: 'row', lg: 'column' },
                    alignItems: { xs: 'center', lg: 'stretch' },
                    gap: { xs: 2, lg: 0 },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(212, 167, 71, 0.15)'
                    }
                  }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, #D4A74720 0%, #D4A74710 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: { xs: 0, lg: 'auto' },
                        mb: { xs: 0, lg: 2 },
                        flexShrink: 0
                      }}
                    >
                      <Trophy size={24} color="#D4A747" />
                    </Box>
                    <Box sx={{ flex: { xs: 1, lg: 'auto' } }}>
                      <Typography variant="h4" sx={{ color: '#D4A747', fontWeight: 700, mb: 1 }}>
                        30+
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('stats.districtsCovered')}
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>

              <Grid size={{ xs: 12, lg: 2.4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card sx={{
                    p: 3,
                    textAlign: { xs: 'left', lg: 'center' },
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(212, 167, 71, 0.1)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: { xs: 'row', lg: 'column' },
                    alignItems: { xs: 'center', lg: 'stretch' },
                    gap: { xs: 2, lg: 0 },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(212, 167, 71, 0.15)'
                    }
                  }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, #B8901C20 0%, #B8901C10 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: { xs: 0, lg: 'auto' },
                        mb: { xs: 0, lg: 2 },
                        flexShrink: 0
                      }}
                    >
                      <Award size={24} color="#B8901C" />
                    </Box>
                    <Box sx={{ flex: { xs: 1, lg: 'auto' } }}>
                      <Typography variant="h4" sx={{ color: '#B8901C', fontWeight: 700, mb: 1 }}>
                        12
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('stats.categories')}
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>

              <Grid size={{ xs: 12, lg: 2.4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card sx={{
                    p: 3,
                    textAlign: { xs: 'left', lg: 'center' },
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(212, 167, 71, 0.1)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: { xs: 'row', lg: 'column' },
                    alignItems: { xs: 'center', lg: 'stretch' },
                    gap: { xs: 2, lg: 0 },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(212, 167, 71, 0.15)'
                    }
                  }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, #D4A52020 0%, #D4A52010 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: { xs: 0, lg: 'auto' },
                        mb: { xs: 0, lg: 2 },
                        flexShrink: 0
                      }}
                    >
                      <MapPin size={24} color="#D4A520" />
                    </Box>
                    <Box sx={{ flex: { xs: 1, lg: 'auto' } }}>
                      <Typography variant="h4" sx={{ color: '#D4A520', fontWeight: 700, mb: 1 }}>
                        120+
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('stats.zonalAwards')}
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>

              <Grid size={{ xs: 12, lg: 2.4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card sx={{
                    p: 3,
                    textAlign: { xs: 'left', lg: 'center' },
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(212, 167, 71, 0.1)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: { xs: 'row', lg: 'column' },
                    alignItems: { xs: 'center', lg: 'stretch' },
                    gap: { xs: 2, lg: 0 },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(212, 167, 71, 0.15)'
                    }
                  }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, #F59E0B20 0%, #F59E0B10 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: { xs: 0, lg: 'auto' },
                        mb: { xs: 0, lg: 2 },
                        flexShrink: 0
                      }}
                    >
                      <Trophy size={24} color="#F59E0B" />
                    </Box>
                    <Box sx={{ flex: { xs: 1, lg: 'auto' } }}>
                      <Typography variant="h4" sx={{ color: '#F59E0B', fontWeight: 700, mb: 1 }}>
                        900+
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('stats.districtAwards')}
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>

              <Grid size={{ xs: 12, lg: 2.4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Card sx={{
                    p: 3,
                    textAlign: { xs: 'left', lg: 'center' },
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(212, 167, 71, 0.1)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: { xs: 'row', lg: 'column' },
                    alignItems: { xs: 'center', lg: 'stretch' },
                    gap: { xs: 2, lg: 0 },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(212, 167, 71, 0.15)'
                    }
                  }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, #EF444420 0%, #EF444410 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: { xs: 0, lg: 'auto' },
                        mb: { xs: 0, lg: 2 },
                        flexShrink: 0
                      }}
                    >
                      <Award size={24} color="#EF4444" />
                    </Box>
                    <Box sx={{ flex: { xs: 1, lg: 'auto' } }}>
                      <Typography variant="h4" sx={{ color: '#EF4444', fontWeight: 700, mb: 1 }}>
                        1000+
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('stats.winners')}
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ pt: { xs: 6, md: 1 }, pb: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          {/* Winners Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress sx={{ color: 'primary.main' }} />
            </Box>
          ) : filteredWinners.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: '10px',
                backgroundColor: 'white'
              }}
            >
              <Trophy size={48} color="#ADB5BD" style={{ marginBottom: 16 }} />
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('filters.noResults.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('filters.noResults.message')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredWinners.map((winner, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={winner._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -6 }}
                  >
                    <Card sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      backgroundColor: 'white',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(212, 167, 71, 0.1)',
                      '&:hover': {
                        boxShadow: '0 15px 45px rgba(212, 167, 71, 0.2)',
                        border: '1px solid rgba(212, 167, 71, 0.3)',
                        '& .winner-photo': {
                          transform: 'scale(1.08)'
                        }
                      }
                    }}>
                      {/* Photo */}
                      <Box
                        sx={{
                          width: '100%',
                          height: 240,
                          position: 'relative',
                          overflow: 'hidden',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }}
                      >
                        <Box
                          component="img"
                          src={winner.photoUrl}
                          alt={winner.name}
                          className="winner-photo"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />

                        {/* Gradient overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '60%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)'
                          }}
                        />

                        {/* Award badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                            color: '#92400E',
                            px: 2,
                            py: 0.5,
                            borderRadius: '50px',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          {winner.awardType}
                        </Box>

                        {/* Name overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: 2
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: 'white',
                              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                              fontSize: '1rem',
                              mb: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {winner.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize'
                              }}
                            >
                              {winner.awardCategory?.category}
                            </Typography>
                            <Box
                              sx={{
                                width: 3,
                                height: 3,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)'
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                                fontWeight: 600,
                                fontSize: '0.7rem'
                              }}
                            >
                              {winner.district}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <CardContent sx={{ p: 2, flex: 1 }}>
                        {/* Award name */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1.5
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '10px',
                              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <Trophy size={16} color="white" />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#1A1A1A',
                                lineHeight: 1.3,
                                fontSize: '0.85rem',
                                mb: 0.5
                              }}
                            >
                              {winner.awardCategory?.awardName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  color: 'text.secondary'
                                }}
                              >
                                {winner.year}
                              </Typography>
                              {winner.zone && (
                                <>
                                  <Box
                                    sx={{
                                      width: 3,
                                      height: 3,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(0, 0, 0, 0.3)'
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      color: '#B8901C'
                                    }}
                                  >
                                    {winner.zone}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Winners;
